import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { areContextsEqual, getTemplateEntry } from '../utils/templates';
import {
  getInitialStyle,
  defaultContext,
  getFocusedStyle,
  PADDING,
  unFocusedStyle,
  getAdaptiveSizes,
} from './banner.style';
import { templates, sizes, imageOptions } from './banner.constants';

/**
 * Banner
 * The Banner functions as a promotional image for programs, videos, or events, utilizing two specific aspect ratios: 1:1 and 3:1, depending on breakpoints.
 *
 * @see https://zeroheight.com/726d5c282/v/latest/p/8893cd-banner/b/99b98d
 */

export default class Banner extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        Content: {
          Description: { text: {} },
          Title: { text: {} },
          Logo: {},
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _attach() {
    this.tag('Image').on('txLoaded', () => {
      this.tag('Image').setSmooth('alpha', 1);
    });
  }

  _detach() {
    this.tag('Image').removeAllListeners('txLoaded');
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = defaultContext.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    if (this._item && this._item.id === content.id && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;

    const { screenSize } = this._style;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);
    this._style = { screenSize, colorMode: colorScheme };

    const { description, title, image, logo } = this._item;
    const imageUrl = (image && image.src) || '';
    const logoUrl = (logo && logo.src) || '';
    const wordWrapWidth = this._config.w - PADDING * 2;

    const adaptiveSizes = getAdaptiveSizes(this._config.w, this._config.h);

    this.patch(
      _merge(
        {
          Image: {
            src: imageUrl,
            alpha: 0.0001,
          },
          Content: {
            Description: {
              text: {
                text: description || '',
                visible: Boolean(description),
                wordWrapWidth,
              },
            },
            Title: {
              text: {
                text: title || '',
                visible: Boolean(title),
                wordWrapWidth,
              },
            },
            Logo: {
              src: logoUrl,
              visible: Boolean(logoUrl),
            },
          },
        },
        adaptiveSizes,
      ),
    );
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Banner.Templates = templates;
Banner.Sizes = sizes;
Banner.ImageOptions = imageOptions;
